import { useCallback, useEffect, useState } from 'react';
import { waitUntil } from '../../../common/utils/functionUtils';
import { manuallyReportError } from '../../analytics/rollbarService';
import { reloadOneTrustBanner } from './oneTrustUtils';
import { isInIFrame } from '../../utils/services/iframe';
import getClientConfig from '../../utils/getClientConfig';
import { ONE_TRUST_STATUS } from './oneTrustConstants';

const oneTrustLoaded = () => !!window.OneTrust && !!window.OneTrust.Init && !!window.OneTrust.GetDomainData;

const useOneTrustLoader = ({ isGuest, userId, userOTToken }) => {
    const config = getClientConfig();

    const [oneTrustStatus, setOneTrustStatus] = useState(ONE_TRUST_STATUS.UNFETCHED);

    const loadOneTrust = useCallback(() => {
        // If OneTrust has not been loaded yet, embed OneTrust script into the DOM
        if (oneTrustStatus === ONE_TRUST_STATUS.UNFETCHED) {
            const { domainScript } = config.oneTrust;

            const script = document.createElement('script');
            script.src = `https://cdn.cookielaw.org/consent/${domainScript}/otSDKStub.js`;
            script.dataset.domainScript = domainScript;

            document.body.appendChild(script);

            setOneTrustStatus(ONE_TRUST_STATUS.FETCHING);

            waitUntil(oneTrustLoaded, { duration: 60000 })
                .then(() => {
                    setOneTrustStatus(ONE_TRUST_STATUS.INITIALISED);
                })
                .catch((error) => {
                    setOneTrustStatus(ONE_TRUST_STATUS.ERROR);
                    manuallyReportError({ errorMessage: 'OneTrust failed to load', error });
                });

            return;
        }

        // If OneTrust has previously been loaded, just reload the cookie banner
        if (oneTrustStatus === ONE_TRUST_STATUS.INITIALISED) {
            reloadOneTrustBanner();
        }
    }, [oneTrustStatus]);

    useEffect(() => {
        // If in iframe, don't show cookie banner, and use default cookie settings per region
        if (isInIFrame()) return;

        // If user authenticated, set user information and load OneTrust
        // - This will be used to get user's cookie preference from OneTrust cloud, or send user's cookie preference
        //   to OneTrust Cloud
        // - This will help in Safari, where user's local cookie pref is removed by Safari every 7 days due to ITP
        if (!isGuest && userId && userId !== 'guest') {
            window.OneTrust = window.OneTrust || {};
            window.OneTrust.dataSubjectParams = {
                id: userId,
                isAnonymous: false,
                token: userOTToken,
            };

            loadOneTrust();
        }

        if (isGuest) {
            window.OneTrust = window.OneTrust || {};
            window.OneTrust.dataSubjectParams = {};

            loadOneTrust();
        }
    }, [isGuest, userId]);

    return [oneTrustStatus];
};

export default useOneTrustLoader;
